import { Box, Container, Grid, List, ListItem, Typography } from "@mui/material";
import { designTokens } from "../../theme/designTokens";
import type { ServiceSectionData } from "../../theme/servicesContent";

const { colors, fonts, spacing } = designTokens;

const SECTION_BG = "#353535";

type ServiceSectionProps = Pick<
  ServiceSectionData,
  "title" | "paragraphs" | "items" | "image" | "imageAlt" | "variant"
>;

const ServiceSection = ({
  title,
  paragraphs,
  items,
  image,
  imageAlt,
  variant,
}: ServiceSectionProps) => {
  const isDark = variant === "dark";
  const textColor = isDark ? colors.white : colors.textPrimary;

  return (
    <Box sx={{ backgroundColor: isDark ? SECTION_BG : colors.white }}>
      <Container maxWidth={spacing.containerMax} sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: fonts.weights.bold,
                color: textColor,
                fontSize: "1rem",
                mb: 2,
              }}
            >
              {title}
            </Typography>

            {paragraphs.map((paragraph) => (
              <Typography
                key={paragraph}
                variant="body2"
                sx={{
                  color: textColor,
                  fontSize: "0.9375rem",
                  lineHeight: "26px",
                  mb: paragraphs.length > 1 ? 1 : 0,
                }}
              >
                {paragraph}
              </Typography>
            ))}

            <List
              sx={{
                listStyleType: "disc",
                pl: 3,
                mt: 2,
                color: textColor,
                "& .MuiListItem-root": {
                  display: "list-item",
                  py: 0,
                  fontSize: "0.9375rem",
                  lineHeight: "26px",
                },
              }}
            >
              {items.map((item) => (
                <ListItem key={item} disablePadding sx={{ color: textColor }}>
                  {item}
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Box
                component="img"
                src={image}
                alt={imageAlt}
                sx={{
                  width: "100%",
                  maxWidth: { xs: 320, sm: 396, md: 472 },
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ServiceSection;
